import React, { useMemo } from 'react';
import _find from 'lodash/find';
import _get from 'lodash/get';
import classNames from 'classnames';
import {
  components as DefaultSelectComponents,
  OptionProps,
} from 'react-select';
import { SelectOption } from '@lib/components/Select/types';
import getWidgetIconSource from 'routes/Home/utils/getWidgetIconSource';
import { AttachmentWithIntendedUse } from 'routes/Home/utils/findAttachmentByIntendedUse';
import WidgetIconRenderer from './WidgetIconRenderer';
import styles from './WidgetOption.module.scss';

interface WidgetOptionData {
  kind?: string;
  icon?: string;
  description?: string;
  attachmentsAttached?: AttachmentWithIntendedUse[];
  fields?: {
    link?: string;
  };
}

function WidgetOption({
  children,
  ...restProps
}: OptionProps): React.ReactElement {
  const { isSelected, options } = restProps;
  const value = _get(restProps, 'value', '');
  const selectedOption = _find(
    options,
    ({ value: optionValue }) => value === optionValue,
  ) as SelectOption;
  const widgetData = selectedOption?.data as WidgetOptionData | undefined;

  const kind = widgetData?.kind ?? '';
  const icon = widgetData?.icon ?? 'widgets';
  const description = widgetData?.description ?? '';
  const attachmentsAttached = widgetData?.attachmentsAttached;
  const link = widgetData?.fields?.link;

  const iconSource = useMemo(
    () =>
      getWidgetIconSource({
        kind,
        attachmentsAttached,
        link,
        materialIcon: icon,
      }),
    [kind, attachmentsAttached, link, icon],
  );

  return (
    <DefaultSelectComponents.Option
      {...restProps}
      className={classNames(styles.widgetOption, {
        [styles.widgetOptionActive]: isSelected,
      })}
    >
      <div className={styles.widgetOptionContent}>
        <div className={styles.widgetOptionIcon}>
          <WidgetIconRenderer
            iconSource={iconSource}
            imageClassName={styles.widgetOptionIconImage}
            faviconClassName={styles.widgetOptionFavicon}
          />
        </div>
        <div className={styles.widgetOptionText}>
          <div className={styles.widgetOptionTitle}>{children}</div>
          {description && (
            <div className={styles.widgetOptionDescription}>{description}</div>
          )}
        </div>
      </div>
    </DefaultSelectComponents.Option>
  );
}

export default WidgetOption;
